import {
    IdcardOutlined,
    DownloadOutlined,
    CheckCircleOutlined,
    SearchOutlined,
    ClearOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    DatePicker,
    Row,
    Col,
    Input,
    Table,
    Select,
    message,
    Modal,
} from "antd";
import { useState, useContext, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { AuthContext } from "../../context/AuthContext";
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

export default function AttendanceSite() {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(100);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const isMobile = window.innerWidth < 768;

    const { authData, backUrl } = useContext(AuthContext);

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    // Separate state for form inputs and applied filters
    const [filterInputs, setFilterInputs] = useState({
        emp_code: "",
        emp_name: "",
        organisation: "",
        department: "",
        designation: "",
        from_date: null,
        to_date: null,
        status: "", // Added status filter
    });

    const [appliedFilters, setAppliedFilters] = useState({
        emp_code: "",
        emp_name: "",
        organisation: "",
        department: "",
        designation: "",
        from_date: null,
        to_date: null,
        status: "", // Added status filter
    });

    // Permission modal state
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [remarks, setRemarks] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Check if user is payroll
    const [isPayroll, setIsPayroll] = useState(false);

    useEffect(() => {
        if (authData?.employee?.user?.username === "BCPL569") {
            setIsPayroll(true);
        }
    }, [authData]);

    // ✅ Fetch attendance data with filters + pagination
    const fetchData = async (filters, page, size) => {
        setLoading(true);
        try {
            const params = {};

            // Add filters to params only if they have values
            if (filters.emp_code?.trim()) params.emp_code = filters.emp_code.trim();
            if (filters.emp_name?.trim()) params.emp_name = filters.emp_name.trim();
            if (filters.organisation) params.organisation = filters.organisation;
            if (filters.department) params.department = filters.department;
            if (filters.designation) params.designation = filters.designation;
            if (filters.from_date)
                params.from_date = dayjs(filters.from_date).format("YYYY-MM-DD");
            if (filters.to_date)
                params.to_date = dayjs(filters.to_date).format("YYYY-MM-DD");
            if (filters.status) params.status = filters.status; // Added status filter

            console.log("Fetching with params:", params); // Debug log

            const res = await axios.get(`${backUrl}/api/hr/attendance-siteoffice/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
                params,
            });

            console.log("API Response:", res.data); // Debug log

            // Handle different response structures
            let data = [];
            let count = 0;

            if (Array.isArray(res.data)) {
                // Direct array response
                data = res.data;
                count = res.data.length;
            } else if (res.data.results && Array.isArray(res.data.results)) {
                // Paginated response with results array
                data = res.data.results;
                count = res.data.count || res.data.results.length;
            } else if (res.data.data && Array.isArray(res.data.data)) {
                // Response wrapped in data object
                data = res.data.data;
                count = res.data.count || res.data.data.length;
            } else {
                console.error("Unexpected response structure:", res.data);
                data = [];
                count = 0;
            }

            console.log("Processed data:", data); // Debug log
            console.log("Data count:", count); // Debug log

            // Set the data
            setDataSource(data);
            setTotalItems(count);

            // Show message if no data found and filters are applied
            if (data.length === 0 && Object.keys(params).length > 0) {
                message.info("No records found matching the filters");
            }

        } catch (error) {
            console.error("Error fetching site attendance:", error);
            console.error("Error response:", error.response?.data); // Debug log
            message.error(error.response?.data?.message || "Failed to fetch site attendance data");
            setDataSource([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    };

    const fetchOrganizations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`);
            setOrganisations(res.data.results || res.data);
        } catch (err) {
            console.error("Failed to fetch organizations:", err);
        }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`);
            setDepartments(res.data.results || res.data);
        } catch (err) {
            console.error("Failed to fetch departments:", err);
        }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`);
            setDesignations(res.data.results || res.data);
        } catch (err) {
            console.error("Failed to fetch designations:", err);
        }
    };

    // ✅ Initial load - fetch all data without any filters
    useEffect(() => {
        fetchOrganizations();
        // Fetch initial data without any filters (empty filters object)
        fetchData(appliedFilters, currentPage, pageSize);
    }, []);

    // ✅ Handle Apply Filters button click
    const handleApplyFilters = () => {
        console.log("Applying filters:", filterInputs); // Debug log
        setAppliedFilters({ ...filterInputs });
        setCurrentPage(1); // Reset to first page
        setSelectedRowKeys([]); // Clear selections
        fetchData(filterInputs, 1, pageSize);
    };

    // ✅ Handle Clear Filters button click
    const handleClearFilters = () => {
        const clearedFilters = {
            emp_code: "",
            emp_name: "",
            organisation: "",
            department: "",
            designation: "",
            from_date: null,
            to_date: null,
            status: "", // Added status filter
        };
        setFilterInputs(clearedFilters);
        setAppliedFilters(clearedFilters);
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        setSelectedRowKeys([]); // Clear selections
        // Fetch all data again (without filters)
        fetchData(clearedFilters, 1, pageSize);
    };

    // Handle pagination change
    const handleTableChange = (page, size) => {
        console.log("Pagination change:", page, size); // Debug log
        if (size !== pageSize) {
            // Page size changed
            setPageSize(size);
            setCurrentPage(1);
            fetchData(appliedFilters, 1, size);
        } else {
            // Page number changed
            setCurrentPage(page);
            fetchData(appliedFilters, page, size);
        }
    };

    // Handle permission consideration click
    const handlePermissionClick = (record) => {
        setSelectedRecord(record);
        setRemarks("");
        setIsModalVisible(true);
    };

    // Submit permission consideration
    const handleSubmitPermission = async () => {
        if (!selectedRecord) return;

        if (!remarks.trim()) {
            message.warning("Please enter remarks before submitting");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                attendance_id: selectedRecord.id,
                employee: selectedRecord.Emp_id,
                remarks: remarks,
            };

            await axios.post(
                `${backUrl}/api/hr/attendance-requests/`,
                payload,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );

            message.success("Permission consideration submitted successfully!");
            setIsModalVisible(false);
            setRemarks("");
            setSelectedRecord(null);
            // Refresh current page data
            fetchData(appliedFilters, currentPage, pageSize);
        } catch (error) {
            console.error("Error submitting permission:", error);

            const errorMessage =
                error.response?.data?.data ||
                error.response?.data?.message ||
                "Failed to submit permission consideration";

            message.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle modal cancel
    const handleModalCancel = () => {
        setIsModalVisible(false);
        setRemarks("");
        setSelectedRecord(null);
    };

    // ✅ Table Columns (updated with permission consideration)
    const columns = [
        {
            title: "Employee Code",
            dataIndex: "EmployeeCode",
            key: "EmployeeCode",
            width: 120,
            fixed: 'left',
        },
        {
            title: "Employee Name",
            dataIndex: "EmployeeName",
            key: "EmployeeName",
            width: 180,
            fixed: 'left',
        },
        { title: "Company Name", dataIndex: "Organisation", key: "Organisation", width: 200 },
        { title: "Department", dataIndex: "Department", key: "Department", width: 150 },
        { title: "Designation", dataIndex: "Designation", key: "Designation", width: 150 },
        { title: "Attendance Date", dataIndex: "Date", key: "Date", width: 130 },
        { title: "Work Location", dataIndex: "WorkLocation", key: "WorkLocation", width: 150 },
        { title: "Check-In", dataIndex: "CheckIn", key: "CheckIn", width: 100 },
        {
            title: "Check-In Photo",
            dataIndex: "check_in_photo_url",
            key: "check_in_photo_url",
            width: 120,
            render: (url) =>
                url ? (
                    <img
                        src={url}
                        alt="Check-In"
                        style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4, cursor: "pointer" }}
                        onClick={() => window.open(url, '_blank')}
                    />
                ) : (
                    "-"
                ),
        },
        {
            title: "Check-In Location",
            dataIndex: "CheckInLocation",
            key: "CheckInLocation",
            render: (text, record) =>
                record.check_in_latitude && record.check_in_longitude ? (
                    <a
                        href={`https://www.google.com/maps?q=${record.check_in_latitude},${record.check_in_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1677ff" }}
                    >
                        {text || "View Location"}
                    </a>
                ) : (
                    text || "-"
                ),
        },
        {
            title: "Check In Site",
            dataIndex: "CheckInWorkSite",
            render: (v, record) => (
                <>
                    <p>{v || "-"}</p>
                    {record.onduty_type_check_in && (
                        <> ({record.onduty_type_check_in})</>
                    )}
                </>
            ),
        },
        { title: "Punch-In", dataIndex: "PunchIn", key: "PunchIn", width: 100 },
        { title: "Check-Out", dataIndex: "CheckOut", key: "CheckOut", width: 100 },
        {
            title: "Check-Out Photo",
            dataIndex: "check_out_photo_url",
            key: "check_out_photo_url",
            width: 120,
            render: (url) =>
                url ? (
                    <img
                        src={url}
                        alt="Check-Out"
                        style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4, cursor: "pointer" }}
                        onClick={() => window.open(url, '_blank')}
                    />
                ) : (
                    "-"
                ),
        },
        {
            title: "Check-Out Location",
            dataIndex: "CheckOutLocation",
            key: "CheckOutLocation",
            render: (text, record) =>
                record.check_out_latitude && record.check_out_longitude ? (
                    <a
                        href={`https://www.google.com/maps?q=${record.check_out_latitude},${record.check_out_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1677ff" }}
                    >
                        {text || "View Location"}
                    </a>
                ) : (
                    text || "-"
                ),
        },
        {
            title: "Check Out Site",
            dataIndex: "CheckOutWorkSite",
            render: (v, record) => (
                <>
                    <p>{v || "-"}</p>
                    {record.onduty_type_check_out && (
                        <> ({record.onduty_type_check_out})</>
                    )}
                </>
            ),
        },
        { title: "Punch-Out", dataIndex: "PunchOut", key: "PunchOut", width: 100 },
        { title: "First In Type", dataIndex: "FirstInType", key: "FirstInType", width: 120 },
        { title: "Last Out Type", dataIndex: "LastOutType", key: "LastOutType", width: 120 },
        {
            title: "Status", dataIndex: "AttendanceStatus", key: "AttendanceStatus", render: (v) =>
                v === "Sunday" ? "Week Off" : v
        },
        { title: "Total Time", dataIndex: "TotalTime", key: "TotalTime" },
        // ...(isPayroll
        //     ? [
        //         {
        //             title: "Action",
        //             key: "action",
        //             width: 250,
        //             render: (_, record) => {
        //                 if (record.AttendanceStatus === "Permission") {
        //                     const permissions = record.permissions || [];
        //                     const autoPermission = permissions.find(
        //                         (p) => p.type === "Auto-Deduction"
        //                     );

        //                     const isAutoPermission = record.AttendanceStatus === "Permission" && !!autoPermission;

        //                     return (
        //                         <>
        //                             {
        //                                 isAutoPermission ? (
        //                                     <Button
        //                                         type="primary"
        //                                         icon={<CheckCircleOutlined />}
        //                                         onClick={() => handlePermissionClick(record)}
        //                                         size="small"
        //                                         style={{ backgroundColor: "#54ec08ff", borderColor: "#54ec08ff" }}
        //                                     >
        //                                         Permission
        //                                     </Button>
        //                                 ) : (
        //                                     <p>
        //                                         -
        //                                     </p>
        //                                 )
        //                             }
        //                         </>

        //                     );
        //                 }
        //                 else {
        //                     return (
        //                         <p>
        //                             -
        //                         </p>)
        //                 }
        //             },
        //         },
        //     ]
        //     : []),
    ];

    // ✅ Excel Download
    const handleDownloadExcel = () => {
        const exportData =
            selectedRowKeys.length > 0
                ? dataSource.filter((item) =>
                    selectedRowKeys.includes(item.id || item.EmployeeCode)
                )
                : dataSource;

        if (exportData.length === 0) {
            message.warning("No data to export!");
            return;
        }

        // Column definitions matching Table columns
        const excelColumns = [
            { title: "Employee Code", key: "EmployeeCode" },
            { title: "Employee Name", key: "EmployeeName" },
            { title: "Company Name", key: "Organisation" },
            { title: "Department", key: "Department" },
            { title: "Designation", key: "Designation" },
            { title: "Attendance Date", key: "Date" },
            { title: "Work Location", key: "WorkLocation" },
            { title: "Check-In", key: "CheckIn" },
            { title: "Check-In Photo", key: "check_in_photo_url" },
            { title: "Check-In Location", key: "CheckInLocation" },
            { title: "Check-In Site", key: "CheckInWorkSite" },
            { title: "Punch-In", key: "PunchIn" },
            { title: "Check-Out", key: "CheckOut" },
            { title: "Check-Out Photo", key: "check_out_photo_url" },
            { title: "Check-Out Location", key: "CheckOutLocation" },
            { title: "Check-Out Site", key: "CheckOutWorkSite" },
            { title: "Punch-Out", key: "PunchOut" },
            { title: "First In Type", key: "FirstInType" },
            { title: "Last Out Type", key: "LastOutType" },
            { title: "Total Time", key: "TotalTime" },
            { title: "Status", key: "AttendanceStatus" },
        ];

        // Convert rows to same structure as columns
        const excelRows = exportData.map((row) => {
            const formattedRow = {};
            excelColumns.forEach((col) => {
                formattedRow[col.title] = row[col.key] ?? "";
            });
            return formattedRow;
        });

        // Create workbook
        const worksheet = XLSX.utils.json_to_sheet(excelRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Site Office Attendance");

        XLSX.writeFile(workbook, "SiteOffice_Attendance.xlsx");
        message.success("Excel Downloaded!");
    };

    return (
        <section>
            <center>
                {isMobile ? (
                    <p style={{ fontSize: "15px", textTransform: "uppercase", color: "#0e497b" }}>
                        <IdcardOutlined /> Site Office Attendance Records
                    </p>
                ) : (
                    <h5>
                        <IdcardOutlined /> Site Office Attendance Records
                    </h5>
                )}
            </center>

            {/* 🔹 Filters Card */}
            <Card
                style={{
                    margin: "20px",
                    padding: "20px",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            >
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Employee Code</label>
                        <Input
                            placeholder="Enter Employee Code"
                            value={filterInputs.emp_code}
                            onChange={(e) => setFilterInputs({ ...filterInputs, emp_code: e.target.value })}
                            onPressEnter={handleApplyFilters}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Employee Name</label>
                        <Input
                            placeholder="Enter Employee Name"
                            value={filterInputs.emp_name}
                            onChange={(e) => setFilterInputs({ ...filterInputs, emp_name: e.target.value })}
                            onPressEnter={handleApplyFilters}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Company Name</label>
                        <Select
                            placeholder="Select Company"
                            allowClear
                            style={{ width: "100%" }}
                            value={filterInputs.organisation || undefined}
                            onChange={(value) => {
                                setFilterInputs({
                                    ...filterInputs,
                                    organisation: value,
                                    department: "",
                                    designation: "",
                                });
                                if (value) {
                                    fetchDepartments(value);
                                } else {
                                    setDepartments([]);
                                    setDesignations([]);
                                }
                            }}
                        >
                            {organisations.map((org) => (
                                <Option key={org.id} value={org.id}>
                                    {org.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Department</label>
                        <Select
                            showSearch
                            placeholder="Select Department"
                            allowClear
                            style={{ width: "100%" }}
                            value={filterInputs.department || undefined}
                            onChange={(value) => {
                                setFilterInputs({ ...filterInputs, department: value, designation: "" });
                                if (value) {
                                    fetchDesignations(value);
                                } else {
                                    setDesignations([]);
                                }
                            }}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {departments.map((dept) => (
                                <Option key={dept.id} value={dept.id}>
                                    {dept.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginTop: "10px" }}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Designation</label>
                        <Select
                            showSearch
                            placeholder="Select Designation"
                            allowClear
                            style={{ width: "100%" }}
                            value={filterInputs.designation || undefined}
                            onChange={(value) => setFilterInputs({ ...filterInputs, designation: value })}
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {designations.map((des) => (
                                <Option key={des.id} value={des.id}>
                                    {des.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Attendance Status</label>
                        <Select
                            placeholder="Select Status"
                            allowClear
                            style={{ width: "100%" }}
                            value={filterInputs.status || undefined}
                            onChange={(value) => setFilterInputs({ ...filterInputs, status: value })}
                        >
                            <Option value="PRESENT">Present</Option>
                            <Option value="PERMISSION">Permission</Option>
                            <Option value="ABSENT">Absent</Option>
                            <Option value="HOLIDAY">Holiday</Option>
                            <Option value="Weekoff">Week Off</Option>
                            <Option value="Compoff">Comp Off</Option>
                            <Option value="CASUAL LEAVE">Casual Leave</Option>
                            <Option value="SICK LEAVE">Sick Leave</Option>
                            <Option value="HALFDAY_LEAVE">Half Day</Option>
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={10} lg={6}>
                        <label>Date Range</label>
                        <RangePicker
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            value={
                                filterInputs.from_date && filterInputs.to_date
                                    ? [dayjs(filterInputs.from_date), dayjs(filterInputs.to_date)]
                                    : null
                            }
                            onChange={(dates) =>
                                setFilterInputs({
                                    ...filterInputs,
                                    from_date: dates ? dates[0] : null,
                                    to_date: dates ? dates[1] : null,
                                })
                            }
                        />
                    </Col>

                    {/* ✅ Filter Action Buttons */}
                    <Col xs={24} sm={24} md={24} lg={6} style={{ display: "flex", alignItems: "flex-end", gap: "10px" }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleApplyFilters}
                            loading={loading}
                            style={{
                                backgroundColor: "#0e497b",
                                borderColor: "#0e497b",
                                height: "36px",
                                flex: 1,
                            }}
                        >
                            Apply Filters
                        </Button>
                        <Button
                            icon={<ClearOutlined />}
                            onClick={handleClearFilters}
                            style={{
                                height: "36px",
                                flex: 1,
                            }}
                        >
                            Clear Filters
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* 🔹 Download Button */}
            <div style={{ marginLeft: "20px", marginTop: "15px" }}>
                <Button
                    icon={<DownloadOutlined />}
                    style={{
                        height: "36px",
                        width: "220px",
                        backgroundColor: "#0e497b",
                        color: "white",
                    }}
                    onClick={handleDownloadExcel}
                    disabled={dataSource.length === 0}
                >
                    Download {selectedRowKeys.length > 0 ? `Selected (${selectedRowKeys.length})` : 'All'}
                </Button>
            </div>

            {/* 🔹 Table */}
            <Table
                style={{ marginTop: "20px", margin: "20px" }}
                columns={columns}
                dataSource={dataSource}
                bordered
                loading={loading}
                pagination={{
                    current: currentPage,
                    pageSize,
                    total: totalItems,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    onChange: handleTableChange,
                    showTotal: (total, range) => total > 0 ? `${range[0]}-${range[1]} of ${total} records` : '0 records',
                }}
                scroll={{ x: 3200, y: 600 }}
                rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys),
                    preserveSelectedRowKeys: true,
                }}
                rowKey={(record) => record.id || record.EmployeeCode}
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: "#0e497b",
                                    color: "white",
                                    fontWeight: "600",
                                }}
                            >
                                {props.children}
                            </th>
                        ),
                    },
                }}
            />

            {/* Permission Consideration Modal */}
            <Modal
                title="Permission Consideration"
                open={isModalVisible}
                onOk={handleSubmitPermission}
                onCancel={handleModalCancel}
                confirmLoading={submitting}
                okText="Submit"
                cancelText="Cancel"
                width={600}
            >
                {selectedRecord && (
                    <div style={{ marginBottom: "20px" }}>
                        <p>
                            <strong>Employee:</strong> {selectedRecord.EmployeeName} (
                            {selectedRecord.EmployeeCode})
                        </p>
                        <p>
                            <strong>Date:</strong> {selectedRecord.Date}
                        </p>
                        <p>
                            <strong>Department:</strong> {selectedRecord.Department}
                        </p>
                    </div>
                )}
                <div>
                    <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                        Remarks:
                    </label>
                    <TextArea
                        rows={4}
                        placeholder="Enter your remarks here..."
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        maxLength={500}
                        showCount
                    />
                </div>
            </Modal>
        </section>
    );
}